import React, { useMemo } from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress, Stack } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { usePerformanceReportingContext } from '../PerformanceReportingContext';
import useTrends from '../_data_sources/useTrends';
import { TrendSeries } from '../_types';
import { neutral, success, error, warning } from '_lex/_foundations/phxColors';

dayjs.extend(customParseFormat);

// Date-only strings (YYYY-MM-DD) parsed as local time.
function formatPeriodLabel(period: string, periodMode: 'week' | 'month'): string {
  const date = dayjs(period, 'YYYY-MM-DD', true);
  if (periodMode === 'week') {
    return `Week of ${date.format('MMM D')}`;
  }
  return date.format('MMM YYYY');
}

function getSeriesLabel(assignedModule: string, seriesName: string): string {
  // Custom labels for use-of-force
  if (assignedModule === 'use-of-force') {
    if (seriesName === 'total') {
      return 'Total UOF Incidents';
    }
    if (seriesName === 'fatalities') {
      return 'Incidents with Fatalities';
    }
  }

  // Default: capitalize first letter
  return seriesName.charAt(0).toUpperCase() + seriesName.slice(1);
}

export function TrendsChart() {
  const { orgId, childOrgId, queryParams } = usePerformanceReportingContext();
  const { data: trendsData, isLoading } = useTrends({
    orgId,
    childOrgId,
    assignedModule: queryParams.assignedModule,
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
  });

  const chartData = useMemo(() => {
    if (!trendsData || !trendsData.series || trendsData.series.length === 0) {
      return null;
    }

    // Get all unique periods from all series
    const allPeriods = new Set<string>();
    trendsData.series.forEach((series) => {
      series.data.forEach((point) => {
        allPeriods.add(point.period);
      });
    });

    const sortedPeriods = Array.from(allPeriods).sort();

    // Format x-axis labels
    const xAxisData = sortedPeriods.map((period) => formatPeriodLabel(period, trendsData.periodMode));

    // Build series data
    const series = trendsData.series.map((trendSeries, index) => {
      // Create a map of period to value for this series
      const periodMap = new Map(trendSeries.data.map((point) => [point.period, point.value]));

      // Get values for all periods, using 0 if missing
      const values = sortedPeriods.map((period) => periodMap.get(period) ?? 0);

      // Determine color based on series index: success for first, error for second, warning for third
      let color = success[600]; // Default to success for first series
      if (index === 0) {
        color = success[600];
      } else if (index === 1) {
        color = error[700];
      } else if (index === 2) {
        color = warning[700];
      }

      return {
        id: trendSeries.name,
        label: getSeriesLabel(queryParams.assignedModule, trendSeries.name),
        data: values,
        color,
        area: true,
        showMark: false,
      };
    });

    return { xAxisData, series, sortedPeriods };
  }, [trendsData, queryParams.assignedModule]);

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
        <CardHeader title='Trends' />
        <CardContent>
          <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!trendsData || !chartData) {
    return null;
  }

  // Generate gradient IDs for each series
  const gradientIds = chartData.series.map((s) => `${s.id}Gradient`);

  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
      <CardHeader title='Trends' />
      <CardContent>
        <Box sx={{ width: '100%', height: 300, position: 'relative' }}>
          {/* SVG Gradient Definitions */}
          <svg style={{ position: 'absolute', width: 0, height: 0 }}>
            <defs>
              {chartData.series.map((series, index) => {
                const gradientId = gradientIds[index];
                const color = series.color as string;
                return (
                  <linearGradient
                    key={gradientId}
                    id={gradientId}
                    x1='0'
                    y1='0'
                    x2='0'
                    y2='1'
                  >
                    <stop
                      offset='0%'
                      stopColor={color}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset='100%'
                      stopColor={color}
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                );
              })}
            </defs>
          </svg>

          <LineChart
            xAxis={[{ scaleType: 'point', data: chartData.xAxisData }]}
            yAxis={[{ min: 0 }]}
            series={chartData.series}
            height={300}
            margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
            grid={{ horizontal: false, vertical: false }}
            slotProps={{
              legend: { hidden: true },
            }}
            sx={{
              '& .MuiLineElement-root': {
                strokeWidth: 2,
              },
              ...chartData.series.reduce(
                (acc, series, index) => {
                  acc[`& .MuiAreaElement-series-${series.id}`] = {
                    fill: `url(#${gradientIds[index]})`,
                  };
                  return acc;
                },
                {} as Record<string, any>
              ),
            }}
          />
        </Box>

        {/* Legend */}
        <Box
          sx={{
            pt: 4,
            pb: 2,
          }}
        >
          <Stack
            direction='row'
            spacing={4}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {chartData.series.map((series) => (
              <Box
                key={series.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: series.color as string,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    color: neutral[500],
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    lineHeight: '16.8px',
                  }}
                >
                  {series.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TrendsChart;
